import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, ErrorBanner } from "../components.jsx";

const SEED_CATEGORIES = ["triage", "triage_scenario", "sdoh", "instructions"];
const SEED_SPECIALTIES = ["oncology", "orthopedics"];

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_-\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");
}

export default function LibraryProtocolNew() {
  const nav = useNavigate();

  const [existingCategories, setExistingCategories] = useState([...SEED_CATEGORIES]);
  const [existingSpecialties, setExistingSpecialties] = useState([...SEED_SPECIALTIES]);

  const [form, setForm] = useState({
    categoryMode: "existing",
    category: "triage",
    newCategory: "",
    protocol_id: "",
    name: "",
    description: "",
    specialties: [],
    customSpecialty: "",
    body_parts: "",
    content: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Discover existing categories + specialties from the current DB
  useEffect(() => {
    (async () => {
      try {
        const res = await api.listProtocols({});
        const cats = new Set(SEED_CATEGORIES);
        const specs = new Set(SEED_SPECIALTIES);
        for (const p of res.data || []) {
          if (p.category) cats.add(p.category);
          for (const s of p.specialties || []) specs.add(s);
        }
        setExistingCategories([...cats].sort());
        setExistingSpecialties([...specs].sort());
      } catch {
        // ignore — fall back to seed values
      }
    })();
  }, []);

  const effectiveCategory =
    form.categoryMode === "new" ? slugify(form.newCategory) : form.category;

  const toggleSpecialty = (s) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter((x) => x !== s)
        : [...f.specialties, s],
    }));
  };

  const addCustomSpecialty = () => {
    const s = form.customSpecialty.trim().toLowerCase();
    if (!s) return;
    if (!form.specialties.includes(s)) {
      setForm((f) => ({
        ...f,
        specialties: [...f.specialties, s],
        customSpecialty: "",
      }));
      if (!existingSpecialties.includes(s)) {
        setExistingSpecialties((arr) => [...arr, s].sort());
      }
    } else {
      setForm((f) => ({ ...f, customSpecialty: "" }));
    }
  };

  const save = async () => {
    setError(null);
    if (!effectiveCategory) {
      setError(new Error("Category is required"));
      return;
    }
    if (!form.protocol_id.trim()) {
      setError(new Error("Protocol ID is required"));
      return;
    }
    if (!form.name.trim()) {
      setError(new Error("Name is required"));
      return;
    }
    if (!form.content.trim()) {
      setError(new Error("Content is required"));
      return;
    }
    if (form.specialties.length === 0) {
      setError(new Error("At least one specialty must be selected"));
      return;
    }

    setBusy(true);
    try {
      const body_parts = form.body_parts
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      await api.writeProtocol(effectiveCategory, slugify(form.protocol_id), {
        content: form.content,
        mode: "draft",
        name: form.name.trim(),
        description: form.description.trim(),
        specialties: form.specialties,
        body_parts: body_parts.length > 0 ? body_parts : null,
        author: "admin",
        commit_msg: `Create ${effectiveCategory}/${form.protocol_id} from admin UI`,
      });

      nav(
        `/library/draft/protocols/${encodeURIComponent(effectiveCategory)}/${encodeURIComponent(
          slugify(form.protocol_id)
        )}`
      );
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="New Protocol"
        subtitle="Create a clinical protocol that agents can reference via tool calls."
        breadcrumbs={[
          { label: "Library", to: "/library/draft/platform" },
          { label: "Agent Rules", to: "/library/draft/protocols" },
          { label: "New" },
        ]}
        actions={
          <div className="btn-row">
            <button className="btn" onClick={() => nav(-1)} disabled={busy}>
              Cancel
            </button>
            <button className="btn primary" onClick={save} disabled={busy}>
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div className="card">
          <div className="card-header">
            <h3>Category</h3>
          </div>

          <div className="btn-row mb-8">
            <button
              className={`btn sm ${form.categoryMode === "existing" ? "primary" : ""}`}
              onClick={() => setForm((f) => ({ ...f, categoryMode: "existing" }))}
            >
              Use existing
            </button>
            <button
              className={`btn sm ${form.categoryMode === "new" ? "primary" : ""}`}
              onClick={() => setForm((f) => ({ ...f, categoryMode: "new" }))}
            >
              + New category
            </button>
          </div>

          {form.categoryMode === "existing" ? (
            <div className="form-field">
              <label>Existing category</label>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {existingCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-field">
              <label>New category ID</label>
              <input
                type="text"
                placeholder="e.g. cancer_pathways"
                value={form.newCategory}
                onChange={(e) =>
                  setForm((f) => ({ ...f, newCategory: e.target.value }))
                }
              />
              <div className="help">
                Will be slugified to:{" "}
                <span className="mono">{slugify(form.newCategory) || "—"}</span>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Identity</h3>
          </div>

          <div className="form-field">
            <label>Protocol ID</label>
            <input
              type="text"
              placeholder="e.g. breast_cancer or knee"
              value={form.protocol_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, protocol_id: e.target.value }))
              }
            />
            <div className="help">
              Slug: <span className="mono">{slugify(form.protocol_id) || "—"}</span>
            </div>
          </div>

          <div className="form-field">
            <label>Name</label>
            <input
              type="text"
              placeholder="Display name (e.g. Breast Cancer Triage)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>

          <div className="form-field">
            <label>Description (optional)</label>
            <input
              type="text"
              placeholder="Short description shown in the protocol list"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </div>

          <div className="form-field">
            <label>Body parts (comma separated, optional)</label>
            <input
              type="text"
              placeholder="e.g. knee, hip"
              value={form.body_parts}
              onChange={(e) => setForm((f) => ({ ...f, body_parts: e.target.value }))}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Agent groups</h3>
          </div>
          <div className="btn-row mb-8" style={{ flexWrap: "wrap" }}>
            {existingSpecialties.map((s) => {
              const on = form.specialties.includes(s);
              return (
                <button
                  key={s}
                  className={`btn sm ${on ? "primary" : ""}`}
                  onClick={() => toggleSpecialty(s)}
                >
                  {on ? "✓ " : ""}
                  {s}
                </button>
              );
            })}
          </div>
          <div className="btn-row">
            <input
              type="text"
              placeholder="Add custom specialty..."
              value={form.customSpecialty}
              onChange={(e) =>
                setForm((f) => ({ ...f, customSpecialty: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomSpecialty();
                }
              }}
              style={{ flex: 1, padding: "6px 10px", fontSize: 13 }}
            />
            <button className="btn sm" onClick={addCustomSpecialty}>
              Add
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Content</h3>
          </div>
          <div className="form-field">
            <textarea
              placeholder="Protocol content. For triage, typically starts with 'SYMPTOM:' or 'BODY PART:'. For SDOH, starts with 'DOMAIN:'. For instructions, 'TYPE:'."
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              spellCheck="false"
            />
            <div className="help">{form.content.length.toLocaleString()} chars</div>
          </div>
        </div>
      </div>
    </>
  );
}
